// src/components/superadmin/CommissionSharing.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CommissionSharing.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const CURRENCIES = ['USD'];

const CommissionSharing = () => {
  const [wallets, setWallets] = useState([]);
  const [shares, setShares] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [ratePercent, setRatePercent] = useState(40);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [w, s, a, r, o] = await Promise.all([
        axios.get(`${API_BASE}/api/super-admin/commission/wallets`, authHeader()),
        axios.get(`${API_BASE}/api/super-admin/commission/shares`, authHeader()),
        axios.get(`${API_BASE}/api/super-admin/commission/analytics`, authHeader()),
        axios.get(`${API_BASE}/api/super-admin/commission/rate`, authHeader()),
        axios.get(`${API_BASE}/api/super-admin/commission/overview`, authHeader())
      ]);
      setWallets(w.data.wallets || []);
      setShares(s.data.shares || []);
      setAnalytics(a.data.admins || []);
      if (r.data.success) setRatePercent(r.data.ratePercent);
      if (o.data.success) setOverview(o.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load commission data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const getShare = (adminId, currency) => {
    const s = shares.find(x => String(x.admin_id) === String(adminId) && x.currency === currency);
    return s ? s.percentage : 0;
  };

  const getWalletBalance = (userId, currency) => {
    const w = wallets.find(x => String(x.user_id) === String(userId) && x.currency === currency);
    return w ? parseFloat(w.balance) : 0;
  };

  const saveRate = async () => {
    try {
      await axios.put(`${API_BASE}/api/super-admin/commission/rate`, { ratePercent }, authHeader());
      setNotice('Commission rate saved');
      loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const setShare = async (adminId, currency, value) => {
    try {
      await axios.put(`${API_BASE}/api/super-admin/commission/shares`,
        { adminId, currency, percentage: value }, authHeader());
      setNotice('Share saved');
      loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const allocate = async (adminId, currency, amount) => {
    try {
      const sa = wallets.find(w => w.role === 'super_admin' && w.currency === currency);
      if (!sa) { setError('Super admin commission wallet not found'); return; }
      await axios.post(`${API_BASE}/api/super-admin/commission/allocate`,
        { fromUserId: sa.user_id, toUserId: adminId, currency, amount }, authHeader());
      setNotice('Commission allocated');
      loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const withdraw = async (currency) => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { setError('Enter a valid withdrawal amount'); return; }
    try {
      await axios.post(`${API_BASE}/api/super-admin/commission/withdraw`,
        { amount, currency }, authHeader());
      setNotice('Withdrawal completed');
      setWithdrawAmount('');
      loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Withdrawal failed');
    }
  };

  const adminRows = analytics.map(a => ({
    ...a,
    wallets: CURRENCIES.map(c => ({
      currency: c,
      balance: getWalletBalance(a.id, c),
      share: getShare(a.id, c)
    }))
  }));

  const superAdminWallets = wallets.filter(w => w.role === 'super_admin' && w.currency === 'USD');

  if (loading) return <div className="cs-loading">Loading commission data…</div>;


  return (
    <div className="cs-page">
      {error && <div className="cs-error">⚠️ {error}</div>}
      {notice && <div className="cs-notice">✓ {notice}</div>}

      <div className="cs-card">
        <h3>⚙️ Admin Commission Rate</h3>
        <div className="cs-rate-row">
          <span>Share of revenue paid to admins:</span>
          <input type="number" min="0" max="100" step="1" value={ratePercent}
            onChange={(e) => setRatePercent(parseFloat(e.target.value) || 0)}
            className="cs-rate-input" /> %
          <button className="cs-save-btn" onClick={saveRate}>Save</button>
        </div>
      </div>

      {overview && (
        <div className="cs-card">
          <h3>📈 Revenue Overview (USD)</h3>
          <div className="cs-overview-grid">
            <div className="cs-ov"><span>Total Revenue (10%)</span><b>${overview.superAdminRevenue?.toFixed(2)}</b></div>
            <div className="cs-ov"><span>Admin Commission Paid</span><b>${overview.adminCommissionPaid?.toFixed(2)}</b></div>
            <div className="cs-ov"><span>Net Revenue</span><b>${overview.net?.toFixed(2)}</b></div>
          </div>
        </div>
      )}

      <div className="cs-card">
        <h3>💰 Super Admin Commission Wallet</h3>
        <div className="cs-wallet-grid">
          {superAdminWallets.length === 0 ? (
            <p className="cs-muted">No commission collected yet.</p>
          ) : superAdminWallets.map(w => (
            <div key={w.id} className="cs-wallet">
              <span className="cs-cur">{w.currency}</span>
              <span className="cs-bal">{parseFloat(w.balance).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="cs-withdraw-row">
          <input type="number" min="0" step="0.01" placeholder="Withdraw amount (USD)"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            className="cs-alloc-input" />
          <button className="cs-save-btn" onClick={() => withdraw('USD')}>Withdraw</button>
        </div>
      </div>

      <div className="cs-card">
        <h3>📊 Admin Analytics (by country)</h3>
        <table className="cs-table">
          <thead>
            <tr><th>Admin</th><th>Country</th><th>Users</th></tr>
          </thead>
          <tbody>
            {adminRows.length === 0 ? (
              <tr><td colSpan={3} className="cs-muted">No admins found.</td></tr>
            ) : adminRows.map(a => (
              <tr key={a.id}>
                <td>{a.username}</td>
                <td>{a.countryName || '—'}</td>
                <td>{a.userCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cs-card">
        <h3>🤝 Commission Sharing</h3>
        {adminRows.length === 0 ? (
          <p className="cs-muted">No admin (distributor) accounts exist yet.</p>
        ) : adminRows.map(a => (
          <div key={a.id} className="cs-admin-row">
            <div className="cs-admin-name">{a.username} <small>({a.countryName || '—'})</small></div>
            {a.wallets.map(w => (
              <div key={w.currency} className="cs-admin-wallet">
                <span className="cs-cur">{w.currency}</span>
                <span className="cs-bal">{w.balance.toFixed(2)}</span>
                <input type="number" min="0" max="100" step="1" defaultValue={w.share}
                  onBlur={(e) => setShare(a.id, w.currency, parseFloat(e.target.value) || 0)}
                  title="Share %" className="cs-share-input" />%
                <input type="number" min="0" step="0.01" placeholder="Allocate"
                  className="cs-alloc-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && parseFloat(e.target.value) > 0) {
                      allocate(a.id, w.currency, parseFloat(e.target.value));
                      e.target.value = '';
                    }
                  }} />
              </div>
            ))}
          </div>
        ))}
        <p className="cs-muted">Tip: type an amount in "Allocate" and press Enter to transfer commission from the super admin wallet to that admin.</p>
      </div>
    </div>
  );
};

export default CommissionSharing;
